import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, X, Send, Bot, RefreshCw, Minimize2, Phone, ExternalLink } from 'lucide-react';

function Message({ msg }) {
  const isBot = msg.role === 'assistant';
  return (
    <div className={`flex gap-2.5 ${isBot ? '' : 'flex-row-reverse'}`}>
      {isBot && (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
          <Bot size={14} className="text-white" />
        </div>
      )}
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed
        ${isBot
          ? 'bg-white text-slate-700 border border-slate-100 shadow-sm rounded-tl-sm'
          : 'bg-primary-600 text-white shadow-sm shadow-primary-600/20 rounded-tr-sm'
        }`}>
        {msg.content}
      </div>
    </div>
  );
}

const SESSION_KEY = 'ai_receptionist_session';

const QUICK_REPLIES = [
  'I need support for my child',
  'I have an NDIS plan',
  'What services do you offer?',
  'I need urgent help',
];

export default function AIReceptionist() {
  const { user } = useAuth();
  const orgName = user?.orgName || 'our team';
  const [open, setOpen]             = useState(false);
  const [minimised, setMin]         = useState(false);
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [sessionId, setSessionId]   = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [unread, setUnread]         = useState(0);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [callNumber, setCallNumber] = useState('');
  const navigate                    = useNavigate();
  const bottomRef                   = useRef(null);
  const inputRef                    = useRef(null);

  useEffect(() => {
    if (messages.length === 0 && open) {
      setMessages([{
        role: 'assistant',
        content: `Hi! I'm the ${orgName} AI Receptionist. I can help with referrals, service enquiries, or connecting you with our team. How can I help you today?`,
      }]);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [open, messages]);

  // Use the org's configured phone for the "Prefer to call?" link (hidden when unset).
  useEffect(() => {
    api.get('/receptionist/my-config')
      .then(r => setCallNumber((r.data?.company_phone || '').trim()))
      .catch(() => {});
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post('/receptionist/chat', { message: text, sessionId });
      if (data.sessionId) {
        setSessionId(data.sessionId);
        localStorage.setItem(SESSION_KEY, data.sessionId);
      }
      setMessages(m => [...m, { role: 'assistant', content: data.reply }]);
      if (data.leadCaptured) setLeadCaptured(true);
      if (!open) setUnread(n => n + 1);
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: 'Sorry, I\'m having trouble right now. Please try again or call us directly.' }]);
    } finally { setLoading(false); }
  };

  const reset = async () => {
    try { await api.post('/receptionist/reset', { sessionId }); } catch {}
    localStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    setLeadCaptured(false);
    setMessages([{
      role: 'assistant',
      content: `Hi! I'm the ${orgName} AI Receptionist. How can I help you today?`,
    }]);
  };

  const onKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {/* Chat window */}
      {open && (
        <div className={`fixed bottom-24 right-6 z-50 w-[360px] bg-white rounded-3xl shadow-2xl border border-slate-200 flex flex-col transition-all overflow-hidden
          ${minimised ? 'h-14' : 'h-[520px]'}`}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-500">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-white/20 ring-1 ring-white/25 flex items-center justify-center">
                <Bot size={16} className="text-white" />
              </div>
              <div>
                <div className="text-white text-sm font-bold">AI Receptionist</div>
                <div className="text-primary-200 text-xs">{user?.orgName || 'Rostery'}</div>
              </div>
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse ml-1" />
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={reset} title="New conversation" aria-label="Start a new conversation"
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                <RefreshCw size={13} aria-hidden="true" />
              </button>
              <button type="button" onClick={() => setMin(m => !m)}
                aria-label={minimised ? 'Expand chat' : 'Minimise chat'} aria-expanded={!minimised}
                title={minimised ? 'Expand chat' : 'Minimise chat'}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                <Minimize2 size={13} aria-hidden="true" />
              </button>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" title="Close chat"
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          </div>

          {!minimised && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                {messages.map((m, i) => <Message key={i} msg={m} />)}
                {loading && (
                  <div className="flex gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shrink-0 shadow-sm">
                      <Bot size={14} className="text-white" />
                    </div>
                    <div className="bg-white border border-slate-100 shadow-sm rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1.5 items-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Lead captured banner */}
              {leadCaptured && (
                <div className="mx-3 mb-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold text-emerald-700">Lead captured ✓</div>
                    <div className="text-xs text-emerald-600">Our team will be in touch soon</div>
                  </div>
                  <button onClick={() => navigate('/receptionist')}
                    className="flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline">
                    View <ExternalLink size={10} />
                  </button>
                </div>
              )}

              {/* Quick replies (only at start) */}
              {messages.length <= 1 && (
                <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                  {QUICK_REPLIES.map(q => (
                    <button key={q} onClick={() => { setInput(q); setTimeout(send, 50); }}
                      className="text-xs px-3 py-1.5 rounded-full border border-primary-200 text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="p-3 border-t border-slate-100 bg-white">
                <div className="flex gap-2 items-end">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={onKey}
                    placeholder="Type a message…"
                    aria-label="Message the receptionist"
                    rows={1}
                    className="flex-1 resize-none border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors max-h-24"
                  />
                  <button
                    type="button"
                    onClick={send}
                    disabled={loading || !input.trim()}
                    aria-label="Send"
                    className="p-2.5 bg-primary-600 text-white rounded-xl shadow-sm shadow-primary-600/25 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none transition-colors">
                    <Send size={15} aria-hidden="true" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-slate-400">Powered by Rostery AI</p>
                  {callNumber && (
                    <a href={`tel:${callNumber.replace(/[^\d+]/g, '')}`} className="flex items-center gap-1 text-xs text-primary-600 font-semibold hover:underline">
                      <Phone size={10} /> Prefer to call?
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* FAB button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close the receptionist chat' : 'Try the receptionist chat'}
        aria-expanded={open}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full shadow-lg shadow-primary-600/30 hover:from-primary-600 hover:to-primary-700 flex items-center justify-center transition-all hover:scale-105 active:scale-95">
        {open
          ? <X size={22} className="text-white" aria-hidden="true" />
          : <MessageCircle size={22} className="text-white" aria-hidden="true" />
        }
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>
    </>
  );
}
